// Client-side parse entry. Prefers the Web Worker (keeps the UI live for large
// libraries), but falls back to main-thread parsing whenever the worker can't
// be constructed or fails to load — Bun's HTML bundler doesn't always emit a
// worker chunk under `bun build` (§17), so the fallback guarantees correctness
// in production regardless.

import type { DistilledProfile } from "./types.ts";
import type { WorkerResponse } from "./parser.worker.ts";

export class ExportRejectedError extends Error {}

async function parseMainThread(
  buffer: ArrayBuffer,
  onProgress?: (file: string, rows: number) => void,
): Promise<DistilledProfile> {
  const { parseZip, NotAnExportError } = await import("./parseZip.ts");
  const { distill } = await import("./distiller.ts");
  try {
    const tables = await parseZip(buffer, (p) => onProgress?.(p.file, p.rows));
    return distill(tables);
  } catch (err) {
    if (err && (err as Error).name === "NotAnExportError") {
      throw new ExportRejectedError((err as Error).message);
    }
    if (err instanceof NotAnExportError) throw new ExportRejectedError(err.message);
    throw err instanceof Error ? err : new Error(String(err));
  }
}

function tryWorker(
  buffer: ArrayBuffer,
  onProgress: ((file: string, rows: number) => void) | undefined,
): Promise<DistilledProfile> | null {
  if (typeof Worker === "undefined") return null;
  let worker: Worker;
  try {
    worker = new Worker(new URL("./parser.worker.ts", import.meta.url), { type: "module" });
  } catch {
    return null;
  }
  return new Promise<DistilledProfile>((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      worker.terminate();
      fn();
    };
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;
      if (msg.type === "progress") onProgress?.(msg.file, msg.rows);
      else if (msg.type === "done") done(() => resolve(msg.profile));
      else if (msg.type === "error")
        done(() =>
          msg.kind === "not-an-export"
            ? reject(new ExportRejectedError(msg.message))
            : reject(new Error(msg.message)),
        );
    };
    // A worker that fails to load (404 chunk, syntax error) rejects with a
    // sentinel so the caller can fall back to main-thread parsing.
    worker.onerror = () => done(() => reject(WORKER_LOAD_FAILED));
    try {
      worker.postMessage({ type: "parse", buffer }, [buffer]);
    } catch {
      done(() => reject(WORKER_LOAD_FAILED));
    }
  });
}

const WORKER_LOAD_FAILED = Symbol("worker-load-failed");

export async function parseExportFile(
  file: File | ArrayBuffer,
  onProgress?: (file: string, rows: number) => void,
): Promise<DistilledProfile> {
  const buffer = file instanceof ArrayBuffer ? file : await file.arrayBuffer();

  const attempt = tryWorker(buffer.slice(0), onProgress);
  if (attempt) {
    try {
      return await attempt;
    } catch (err) {
      if (err !== WORKER_LOAD_FAILED) throw err; // real parse error — surface it
      // else: worker couldn't load; fall through to main thread.
    }
  }
  return parseMainThread(buffer, onProgress);
}
